import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { PayFeeButton } from "@/components/PayFeeButton";
import { PlanComparison } from "@/components/PlanComparison";
import { btn } from "@/components/CaseBits";

export const PlanBanner = ({ kase, role, onDone, payEnabled }) => {
  const [busy, setBusy] = useState(false);
  const basic = kase.plan !== "premium";
  const total = (kase.halves || []).reduce((s, h) => s + h.amount, 0);
  const mine = (kase.halves || []).find((h) => h.side === kase.my_side);

  const upgrade = async () => {
    setBusy(true);
    try {
      await api.post(`/cases/${kase.id}/upgrade`);
      toast.success("Upgrade fee notes raised — one half payable by each side");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (role === "arbitrator") {
    return (
      <div className={`mt-8 border p-5 ${kase.fully_activated ? "border-green-700/40 bg-green-50/60 dark:bg-green-950/20" : "border-amber-700/40 bg-amber-50 dark:bg-amber-950/30"}`}
        data-testid="registry-status-banner">
        <p className="label-caps">Registry status</p>
        <p className="text-sm mt-2 max-w-3xl" data-testid="registry-status-text">
          {kase.fully_activated
            ? "Both parties have completed their registry formalities."
            : "The parties' registry formalities are not yet complete. You may still post and schedule sittings; an unpaid side does not receive the record until it settles its half."}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-4" data-testid="plan-banner">
      <div className="border border-border bg-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="label-caps">Case fee</p>
          <p className="text-sm mt-2">
            <Badge className={basic ? "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200" : "bg-green-50 text-green-900"} testId="plan-badge">
              {basic ? "Basic" : "Full access"} · {money(total)} shared equally
            </Badge>
            <span className="ml-3 text-muted-foreground">{kase.plan_label}</span>
          </p>
        </div>
        {basic && role !== "arbitrator" && (
          <Button data-testid="upgrade-plan-button" onClick={upgrade} disabled={busy} className={btn}>
            {busy ? "Raising…" : "Upgrade to full access"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="fee-split-panel">
        {["claimant", "respondent"].map((side) => {
          const h = (kase.halves || []).find((x) => x.side === side);
          const paid = side === "claimant" ? kase.claimant_paid : kase.respondent_paid;
          return (
            <div key={side} className={`border p-5 ${paid ? "border-green-700/40 bg-green-50/60 dark:bg-green-950/20" : "border-amber-700/40 bg-amber-50 dark:bg-amber-950/30"}`}
              data-testid={`fee-side-${side}`}>
              <div className="flex items-center justify-between gap-4">
                <p className="label-caps">{side === "claimant" ? "Claimant's half" : "Respondent's half"}</p>
                <Badge className={paid ? "bg-green-100 text-green-900" : "bg-red-50 text-red-900"}
                  testId={`fee-side-status-${side}`}>{paid ? "paid" : "pending"}</Badge>
              </div>
              <p className="font-display text-2xl mt-3" data-testid={`fee-side-amount-${side}`}>
                {h ? money(h.amount) : "—"}
              </p>
              {h && <p className="font-mono text-xs text-muted-foreground mt-1">{h.invoice_number}</p>}
            </div>
          );
        })}
      </div>

      {basic && role !== "arbitrator" && (
        <details className="border border-border bg-card" data-testid="plan-compare-toggle">
          <summary className="px-5 py-4 text-xs uppercase tracking-widest cursor-pointer hover:bg-accent transition-colors duration-200">
            Compare Basic and Full access
          </summary>
          <div className="p-5 pt-0">
            <PlanComparison currentPlan={kase.plan} />
          </div>
        </details>
      )}

      {role !== "arbitrator" && !kase.my_side_paid && mine && (
        <div className="border border-red-800/40 bg-red-50 dark:bg-red-950/30 p-5" data-testid="my-half-pending">
          <p className="label-caps">Your side's half is pending</p>
          <p className="text-sm mt-2 max-w-3xl">
            The case record stays closed to your side until {money(mine.amount)} against
            {" "}{mine.invoice_number} is paid. Your access does not depend on the other side.
          </p>
          <div className="mt-4">
            <PayFeeButton invoice={{ id: mine.invoice_id, amount: mine.amount, currency: "INR" }}
              enabled={payEnabled} onPaid={onDone} />
          </div>
        </div>
      )}

      {!kase.fully_activated && kase.demo_access && (
        <div className="border border-blue-900/40 bg-blue-50 dark:bg-blue-950/30 p-5" data-testid="demo-access-notice">
          <p className="label-caps">Test account — full access open</p>
          <p className="text-sm mt-2 max-w-3xl">
            Fee notes are shown for reference only; e-filing, downloads and the complete record are open to this test
            account without payment so that every feature can be tried.
          </p>
        </div>
      )}
      {!kase.fully_activated && !kase.demo_access && (
        <div className="border border-amber-700/40 bg-amber-50 dark:bg-amber-950/30 p-5" data-testid="subscription-block-notice">
          <p className="label-caps">Awaiting payment — case not fully activated</p>
          <p className="text-sm mt-2 max-w-3xl">
            Each side's access follows its own payment: once your half is paid you get your
            filings, orders and updates in full, whatever the other side has done. The tribunal
            posts hearings independently of payment; an unpaid side does not receive the record.
          </p>
        </div>
      )}
    </div>
  );
};
